import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { TimeSelect } from "@/components/TimeSelect";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatDateIT, todayISO, monthKey, fmtHours } from "@/lib/dates";

function diffHours(start, end) {
  if (!start || !end) return null;
  const [sh, sm] = start.split(":").map(Number);
  const [eh, em] = end.split(":").map(Number);
  const d = eh * 60 + em - (sh * 60 + sm);
  return d > 0 ? d / 60 : null;
}

export default function EmployeeHours() {
  const [jobs, setJobs] = useState([]);
  const [summary, setSummary] = useState({ today_hours: 0, week_hours: 0, month_hours: 0 });
  const [entries, setEntries] = useState([]);
  const [jobId, setJobId] = useState("");
  const [workDate, setWorkDate] = useState(todayISO());
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    const month = monthKey(new Date());
    const [j, s, e] = await Promise.all([
      api.get("/jobs"),
      api.get("/hours/summary"),
      api.get(`/hours/mine?month=${month}`),
    ]);
    setJobs(j.data);
    setSummary(s.data);
    setEntries(e.data);
  };

  useEffect(() => {
    load().catch(() => toast.error("Errore nel caricamento"));
  }, []);

  const preview = diffHours(start, end);

  const weeks = (() => {
    const map = {};
    entries.forEach((e) => {
      const d = new Date(e.work_date + "T00:00:00");
      const wd = (d.getDay() + 6) % 7;
      const s = new Date(d);
      s.setDate(d.getDate() - wd);
      const key = `${s.getFullYear()}-${String(s.getMonth() + 1).padStart(2, "0")}-${String(s.getDate()).padStart(2, "0")}`;
      map[key] = (map[key] || 0) + e.hours;
    });
    const fmt = (dt) => `${String(dt.getDate()).padStart(2, "0")}/${String(dt.getMonth() + 1).padStart(2, "0")}`;
    return Object.keys(map).sort().map((k) => {
      const s = new Date(k + "T00:00:00");
      const e2 = new Date(s);
      e2.setDate(s.getDate() + 6);
      return { label: `${fmt(s)} – ${fmt(e2)}`, hours: Math.round(map[k] * 100) / 100 };
    });
  })();

  const submit = async () => {
    if (!jobId) return toast.error("Seleziona un lavoro");
    if (!start || !end) return toast.error("Inserisci ora di inizio e fine");
    if (preview === null) return toast.error("L'ora di fine deve essere dopo l'inizio");
    setSubmitting(true);
    try {
      await api.post("/hours", { job_id: jobId, work_date: workDate, start_time: start, end_time: end, note });
      toast.success("Ore inviate!");
      setStart("");
      setEnd("");
      setNote("");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-5">
      <h1 className="font-head text-2xl font-bold text-slate-900">Le mie ore</h1>

      {/* Totals */}
      <div className="grid grid-cols-3 gap-2">
        <div data-testid="today-total" className="bg-primary text-primary-foreground rounded-2xl p-3">
          <p className="text-[10px] font-semibold uppercase tracking-wide opacity-80">Oggi</p>
          <p className="text-2xl font-head font-extrabold mt-1">{fmtHours(summary.today_hours)}<span className="text-sm font-bold"> h</span></p>
        </div>
        <div data-testid="week-total" className="bg-white border border-slate-200 rounded-2xl p-3">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Settimana</p>
          <p className="text-2xl font-head font-extrabold mt-1 text-slate-900">{fmtHours(summary.week_hours)}<span className="text-sm font-bold text-slate-400"> h</span></p>
        </div>
        <div data-testid="month-total" className="bg-white border border-slate-200 rounded-2xl p-3">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Mese</p>
          <p className="text-2xl font-head font-extrabold mt-1 text-slate-900">{fmtHours(summary.month_hours)}<span className="text-sm font-bold text-slate-400"> h</span></p>
        </div>
      </div>

      {weeks.length > 0 && (
        <div data-testid="weekly-breakdown" className="bg-white border border-slate-200 rounded-2xl p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Riepilogo settimanale</p>
          <div className="space-y-1.5">
            {weeks.map((w) => (
              <div key={w.label} className="flex items-center justify-between text-sm">
                <span className="text-slate-600">{w.label}</span>
                <span className="font-semibold text-slate-900">{fmtHours(w.hours)} h</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Form */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-4">
        <h2 className="font-head text-lg font-bold text-slate-900">Registra ore</h2>

        <div>
          <label className="text-sm font-semibold text-slate-600 mb-1.5 block">Lavoro / Azienda</label>
          <Select value={jobId} onValueChange={setJobId}>
            <SelectTrigger data-testid="job-select" className="h-14 rounded-xl text-lg">
              <SelectValue placeholder="Seleziona il lavoro" />
            </SelectTrigger>
            <SelectContent>
              {jobs.length === 0 && (
                <div className="px-3 py-2 text-sm text-slate-400">Nessun lavoro assegnato</div>
              )}
              {jobs.map((j) => (
                <SelectItem key={j.id} value={j.id} className="text-lg">
                  {j.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="text-sm font-semibold text-slate-600 mb-1.5 block">Data</label>
          <input
            data-testid="work-date"
            type="date"
            value={workDate}
            onChange={(e) => setWorkDate(e.target.value)}
            className="w-full h-14 rounded-xl border border-slate-200 px-4 text-lg outline-none focus:border-primary"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-sm font-semibold text-slate-600 mb-1.5 block">Inizio</label>
            <TimeSelect value={start} onChange={setStart} testid="start" />
          </div>
          <div>
            <label className="text-sm font-semibold text-slate-600 mb-1.5 block">Fine</label>
            <TimeSelect value={end} onChange={setEnd} testid="end" />
          </div>
        </div>

        <div>
          <label className="text-sm font-semibold text-slate-600 mb-1.5 block">Nota <span className="font-normal text-slate-400">(cosa hai fatto — facoltativo)</span></label>
          <textarea
            data-testid="note-input"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            maxLength={300}
            placeholder="Es. Pulizia uffici e scale, svuotamento cestini"
            className="w-full rounded-xl border border-slate-200 px-4 py-3 text-base outline-none focus:border-primary resize-none"
          />
        </div>

        {preview !== null && (
          <div data-testid="hours-preview" className="text-center bg-accent text-accent-foreground rounded-xl py-2 font-bold">
            Totale: {fmtHours(preview)} ore
          </div>
        )}

        <p data-testid="warning-text" className="text-xs text-slate-400 leading-relaxed">
          Attenzione: una volta inviato, l'orario non potrà più essere modificato né cancellato. Solo l'amministratore può correggerlo.
        </p>

        <button
          data-testid="submit-hours-button"
          onClick={submit}
          disabled={submitting}
          className="w-full h-14 rounded-xl bg-primary text-primary-foreground text-lg font-bold flex items-center justify-center gap-2 active:scale-[0.98] transition-transform disabled:opacity-60"
        >
          {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Send className="h-5 w-5" /> Invia ore</>}
        </button>
      </div>

      {/* History */}
      <div>
        <h2 className="font-head text-lg font-bold text-slate-900 mb-2">Registrazioni del mese</h2>
        <div className="space-y-2">
          {entries.length === 0 && (
            <p className="text-slate-400 text-sm">Nessuna registrazione questo mese.</p>
          )}
          {entries.map((e) => (
            <div key={e.id} data-testid="hours-entry" className="bg-white border border-slate-200 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-slate-900">{e.job_name}</p>
                  <p className="text-sm text-slate-500">{formatDateIT(e.work_date)} · {e.start_time} – {e.end_time}</p>
                </div>
                <span className="font-head font-bold text-primary text-lg">{fmtHours(e.hours)} h</span>
              </div>
              {e.note && (
                <p data-testid="entry-note" className="text-sm text-slate-600 mt-2 bg-slate-50 rounded-lg px-3 py-2">{e.note}</p>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
